// offline-prefetch.ts — backlit.offline.prefetch(store, target, opts): warm
// this device's copy of a prefix (or of a list of keys) while the app is
// online, one request at a time.
//
// It asks the remote transport itself (never getBackend(): the decorator's
// own write-through would fold every answer twice) and folds each answer
// through the decorator's fold API (offline-backend.ts handle.fold) — the same
// response-uid gate, purge-epoch check and reconcile as an app's own read —
// awaiting each fold before the next request. It keeps its own listing
// continuity, never the decorator's shared cursor chain.
//
//   - A prefix: a list pass (list pages at limit 1000, each folded — bytes
//     whose crc is unchanged are kept, deleted keys pruned, coverage
//     recorded), then a fill pass over the listed rows still without bytes
//     (a row over maxBytes / 4 is reported "exceeds_max_bytes" and never
//     asked for): one get per key when that takes no more requests than
//     getBatch pages at limit 500 would (m ≤ ceil(n / 500)), else getBatch
//     pages, then one get per row a page left out as "too_large". An unchanged
//     re-run costs only the list requests.
//   - A list of keys: one get per key; a 404 leaves a tombstone and is
//     reported "not_found".
//
// Before every request and every fold it checks, synchronously: the abort
// signal, that the same decorator is still live (disable()), the purge epoch
// (a purge — clear(), logout, a different user — stops it), the store's
// partition and its staleness. It never rejects once its arguments are
// valid: it resolves what it did, with `reason` when it stopped early.

import type { Backend, ListOptions, ListPage } from "./backend.js";
import { BacklitError } from "./errors.js";
import { isStale, isUserPartition, now, partitionFor, type IdentityRecord, type MirrorStoreName } from "./offline-identity.js";
import { isPinned, type MirrorMeta, type PutResult } from "./offline-store.js";
import { responseMetaOf } from "./transport.js";
import { validateDataKey, validateDataPrefix } from "./validate.js";
import type { Interval, Link, OfflineBackendHandle, OfflineContext, Row } from "./offline-backend.js";
import type { OfflinePrefetchProgress, OfflinePrefetchResult } from "./offline.js";

// LIST_LIMIT / BATCH_LIMIT are the page sizes the passes ask for (the
// servers' maxima); MAX_KEYS bounds a key-list target.
const LIST_LIMIT = 1000;
const BATCH_LIMIT = 500;
const MAX_KEYS = 1000;

// PrefetchArgs is prefetch()'s validated arguments.
export interface PrefetchArgs {
  s: MirrorStoreName;
  prefix: string | null; // null: a key-list target
  keys: string[];
  signal: AbortSignal | undefined;
  pin: boolean;
  maxRequests: number; // Infinity = unlimited
  onProgress: ((p: OfflinePrefetchProgress) => void) | undefined;
}

// PrefetchHost is the runtime as prefetch sees it: the shared context, the
// remote transport, the decorator live now (null: offline mode is off) and
// offline.pin.
export interface PrefetchHost {
  readonly ctx: OfflineContext;
  readonly remote: Backend;
  live(): OfflineBackendHandle | null;
  pin(s: MirrorStoreName, prefix: string): Promise<boolean>;
}

const bad = (code: "invalid_argument" | "invalid_key", msg: string) => new BacklitError(code, "offline.prefetch: " + msg);

// prefetchArgs validates prefetch()'s arguments (all but the store's presence
// in options().stores, which needs the enable() in flight settled).
export function prefetchArgs(store: unknown, target: unknown, opts: unknown): PrefetchArgs {
  if (store !== "data" && store !== "userdata" && store !== "records") {
    throw bad("invalid_argument", 'store must be "data", "userdata" or "records"');
  }
  let prefix: string | null = null;
  let keys: string[] = [];
  if (typeof target === "string") {
    validateDataPrefix(target);
    prefix = target;
  } else if (Array.isArray(target)) {
    if (target.length < 1 || target.length > MAX_KEYS) throw bad("invalid_key", "keys must list 1 to " + MAX_KEYS + " keys");
    for (const k of target) validateDataKey(k);
    keys = [...new Set(target as string[])];
  } else {
    throw bad("invalid_argument", "target must be a prefix or an array of keys");
  }
  if (opts !== undefined && (opts === null || typeof opts !== "object")) throw bad("invalid_argument", "invalid options");
  const o = (opts ?? {}) as Record<string, unknown>;
  const signal = o.signal;
  if (signal !== undefined && (signal === null || typeof signal !== "object" || typeof (signal as AbortSignal).aborted !== "boolean")) {
    throw bad("invalid_argument", "signal must be an AbortSignal");
  }
  const pin = o.pin ?? false;
  if (typeof pin !== "boolean") throw bad("invalid_argument", "pin must be a boolean");
  const max = o.maxRequests;
  if (max !== undefined && !(typeof max === "number" && Number.isInteger(max) && max > 0)) {
    throw bad("invalid_argument", "maxRequests must be a positive integer");
  }
  const onProgress = o.onProgress;
  if (onProgress !== undefined && typeof onProgress !== "function") throw bad("invalid_argument", "onProgress must be a function");
  return {
    s: store,
    prefix,
    keys,
    signal: signal as AbortSignal | undefined,
    pin,
    maxRequests: max === undefined ? Infinity : (max as number),
    onProgress: onProgress as PrefetchArgs["onProgress"],
  };
}

// idleResult is what prefetch resolves without doing anything (reason says why).
export function idleResult(a: PrefetchArgs, reason: string): OfflinePrefetchResult {
  const phase = a.prefix === null ? "fetch" : "list";
  return { phase, requests: 0, keys: 0, stored: 0, bytes: 0, totalKeys: 0, totalBytes: 0, complete: false, notStored: [], reason };
}

// errReason is the stop reason a failed request gives.
function errReason(e: unknown): string {
  if (!(e instanceof BacklitError)) return "unavailable";
  if (e.code === "network") return "network";
  if (e.status === 429) return "rate_limited";
  if (e.code === "glow_dark" || e.status >= 500) return "unavailable";
  return e.code;
}

const isNotFound = (e: unknown): e is BacklitError => e instanceof BacklitError && e.code === "not_found" && e.status === 404;

// ABORT is what a request race resolves when the signal fires first.
const ABORT: unique symbol = Symbol("abort");

type Asked<T> = { r: T | BacklitError; iv: Interval };

// runPrefetch runs one validated prefetch against decorator h (live now).
export async function runPrefetch(host: PrefetchHost, h: OfflineBackendHandle, a: PrefetchArgs): Promise<OfflinePrefetchResult> {
  const { ctx } = host;
  const { s, signal } = a;
  const remote = host.remote as unknown as Record<string, (x: string, o?: ListOptions) => Promise<unknown>>;
  if (a.prefix !== null && a.pin) await host.pin(s, a.prefix);
  const st: { -readonly [K in keyof OfflinePrefetchProgress]: OfflinePrefetchProgress[K] } = {
    phase: a.prefix === null ? "fetch" : "list",
    requests: 0,
    keys: 0,
    stored: 0,
    bytes: 0,
    totalKeys: 0,
    totalBytes: 0,
  };
  const notStored: Array<{ key: string; reason: string }> = [];
  let reason = "";
  const ep0 = ctx.epoch();
  const p0 = partitionFor(s, ctx.identity());

  // halt is the synchronous check before every request and every fold: the
  // reason to stop now, or "".
  const halt = (): string => {
    if (signal?.aborted) return "aborted";
    if (host.live() !== h) return "disabled";
    if (ctx.epoch() !== ep0 || partitionFor(s, ctx.identity()) !== p0) return "identity_change";
    if (p0 && isUserPartition(p0) && isStale(ctx.identity() as IdentityRecord, now())) return "stale";
    return "";
  };
  const stop = (why: string) => void (reason ||= why);
  const skip = (key: string, why: string) => {
    notStored.push({ key, reason: why });
    st.keys++;
  };
  const progress = () => {
    try {
      a.onProgress?.({ ...st });
    } catch {
      /* the app's callback never breaks the run */
    }
  };

  // The abort signal settles aborted, so a hung request never holds the run.
  let unlisten = () => {};
  const aborted = new Promise<typeof ABORT>((res) => {
    if (!signal || typeof signal.addEventListener !== "function") return;
    const on = () => res(ABORT);
    signal.addEventListener("abort", on, { once: true });
    unlisten = () => signal.removeEventListener("abort", on);
  });

  // ask makes one metered request after the checks (and maxRequests):
  // resolves its answer (a thrown BacklitError is an answer too) and
  // interval, or null — with reason set — when the run stops instead. A
  // failure other than not_found stops the run.
  async function ask<T>(method: string, x: string, o?: ListOptions): Promise<Asked<T> | null> {
    const why = halt() || (st.requests >= a.maxRequests ? "max_requests" : "");
    if (why) return stop(why), null;
    const iv: Interval = { t0: now(), t1: 0, ep: ctx.epoch() };
    st.requests++;
    let r: T | BacklitError | typeof ABORT;
    try {
      r = await Promise.race([(remote[s + method] as (x: string, o?: ListOptions) => Promise<T>)(x, o), aborted]);
    } catch (e) {
      r = e instanceof BacklitError ? e : new BacklitError("internal", String(e), 0);
    }
    iv.t1 = now();
    if (r === ABORT) return stop("aborted"), null;
    if (r instanceof BacklitError && !isNotFound(r)) return stop(halt() || errReason(r)), null;
    const after = halt();
    if (after) return stop(after), null;
    return { r, iv };
  }

  // getOne fetches k and folds the answer; resolves whether to go on.
  async function getOne(k: string): Promise<boolean> {
    const q = await ask<{ value: Blob }>("Get", k);
    if (!q) return false;
    let out: PutResult | "skipped";
    try {
      out = await h.fold.value(s, k, q.r as never, responseMetaOf(q.r), q.iv);
    } catch {
      out = "failed";
    }
    const size = q.r instanceof BacklitError ? 0 : q.r.value.size;
    if (a.prefix === null) st.totalBytes += size;
    if (out === "failed") return stop(halt() || "quota"), false;
    if (out === "skipped" && halt()) return stop(halt()), false;
    if (q.r instanceof BacklitError) skip(k, "not_found");
    else if (out === "stored") {
      st.stored++;
      st.bytes += size;
      st.keys++;
    } else if (out === "meta") skip(k, size > ctx.options().maxBytes ? "exceeds_max_bytes" : "no_room");
    else skip(k, "unverified"); // the answer couldn't be tied to this partition
    progress();
    return true;
  }

  // pages runs one pass of list (0) / getBatch (1) pages over the prefix,
  // folding each; each page is also handed to onPage. Resolves whether the
  // pass reached the end with every page folded.
  async function pages(kind: 0 | 1, prefix: string, onPage: (pg: ListPage<Row>) => void): Promise<boolean> {
    let from: Link = { lastKey: "", fromStart: true };
    let cursor = "";
    for (;;) {
      const o: ListOptions = { limit: kind ? BATCH_LIMIT : LIST_LIMIT, ...(cursor ? { cursor } : {}) };
      const q = await ask<ListPage<Row>>(kind ? "GetBatch" : "List", prefix, o);
      if (!q) return false;
      if (q.r instanceof BacklitError) return stop(errReason(q.r)), false; // a 404 page
      const pg = q.r;
      let folded: boolean;
      try {
        folded = await h.fold.page(kind, s, prefix, from, pg, responseMetaOf(pg), q.iv);
      } catch {
        return stop(halt() || "quota"), false;
      }
      onPage(pg);
      progress();
      const last = pg.items.length > 0 ? (pg.items[pg.items.length - 1] as Row).key : from.lastKey;
      from = { lastKey: last, fromStart: from.fromStart && folded };
      if (pg.next === "") return from.fromStart;
      cursor = pg.next;
    }
  }

  // scan reads the prefix's metadata rows (after the checks); null when the
  // run stops instead.
  async function scan(prefix: string): Promise<MirrorMeta[] | null> {
    const why = halt();
    if (why || !p0) return stop(why || "identity_change"), null;
    try {
      const ms = await ctx.mirror.scanMeta(s, p0, prefix);
      const after = halt();
      return after ? (stop(after), null) : ms;
    } catch {
      return stop(halt() || "quota"), null;
    }
  }

  // prefixRun is the list pass then the fill pass; resolves complete.
  async function prefixRun(prefix: string): Promise<boolean> {
    const listed = await pages(0, prefix, (pg) => {
      st.totalKeys += pg.items.length;
      for (const it of pg.items) st.totalBytes += it.size;
    });
    if (reason || !listed) return false;
    st.phase = "fetch";
    const ms = await scan(prefix);
    if (!ms) return false;
    const cap = ctx.options().maxBytes / 4;
    const want: string[] = [];
    for (const e of ms) {
      if (e.hasValue) {
        st.stored++;
        st.bytes += e.size;
        st.keys++;
      } else if (e.state === "present" && e.size > cap) skip(e.k, "exceeds_max_bytes");
      else want.push(e.k); // no bytes yet (or an unknown entry)
    }
    if (want.length <= Math.ceil(st.totalKeys / BATCH_LIMIT)) {
      for (const k of want) if (!(await getOne(k))) return false;
    } else {
      const wanted = new Set(want);
      const big: string[] = [];
      const batched = await pages(1, prefix, (pg) => {
        for (const it of pg.items as Array<Row & { omitted?: boolean; reason?: string }>) {
          if (!wanted.has(it.key)) continue;
          if (it.omitted) {
            if (it.reason === "too_large") big.push(it.key);
          } else if (it.crc32c !== "") {
            st.stored++;
            st.bytes += it.size;
            st.keys++;
          }
        }
      });
      if (reason) return false;
      for (const k of big) {
        const w = halt();
        if (w) return stop(w), false;
        let has = false;
        try {
          has = !!(await ctx.mirror.get(s, p0 as string, k))?.value;
        } catch {
          /* ask for it */
        }
        if (!has && !(await getOne(k))) return false;
      }
      if (!batched) return false;
    }
    return recount(prefix);
  }

  // recount re-reads what the device now holds under the prefix: stored and
  // bytes count the values on the device, and every listed key without its
  // bytes that no answer reported is named (a pinned one found no room, any
  // other was evicted to fit maxBytes, an unknown one changed while read).
  async function recount(prefix: string): Promise<boolean> {
    const ms = await scan(prefix);
    if (!ms) return false;
    let pins: Awaited<ReturnType<typeof ctx.mirror.pins>> = [];
    try {
      pins = await ctx.mirror.pins();
    } catch {
      /* none known */
    }
    const w = halt();
    if (w) return stop(w), false;
    // A value a batch page brought after all is no longer "not stored".
    const held = new Set(ms.filter((e) => e.hasValue).map((e) => e.k));
    const told = new Set(notStored.map((x) => x.key));
    for (let i = notStored.length - 1; i >= 0; i--) if (held.has((notStored[i] as { key: string }).key)) notStored.splice(i, 1);
    st.stored = st.bytes = 0;
    for (const e of ms) {
      if (e.hasValue) {
        st.stored++;
        st.bytes += e.size;
      } else if (!told.has(e.k)) {
        notStored.push({ key: e.k, reason: e.state !== "present" ? "changed" : isPinned(pins, s, e.p, e.k) ? "no_room" : "max_bytes" });
      }
    }
    st.keys = st.stored + notStored.length;
    return true;
  }

  let complete = false;
  try {
    const why = halt() || (p0 === null ? "signed_out" : "");
    if (why) stop(why);
    else if (a.prefix !== null) complete = await prefixRun(a.prefix);
    else {
      st.totalKeys = a.keys.length;
      complete = true;
      for (const k of a.keys) {
        if (!(await getOne(k))) {
          complete = false;
          break;
        }
      }
    }
  } finally {
    unlisten();
  }
  return { ...st, complete: complete && !reason, notStored, ...(reason ? { reason } : {}) };
}
